import { Bell, CalendarDays, ChevronLeft, ChevronRight, LogOut, UserRound, BarChart3 } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

const initials = (value = "") => {
  const clean = value.trim();
  if (!clean) return "NA";
  const parts = clean.split(" ");
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const roleLabel = (role = "") => String(role || "").replace(/_/g, " ").replace(/\b\w/g, (match) => match.toUpperCase());

const memberSinceLabel = (createdAt) => {
  const parsed = new Date(createdAt || "");
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleDateString(undefined, { month: "short", year: "numeric" });
};

const profileAvatar = (user = {}) => {
  const mode = String(user.avatar_mode || "").trim().toLowerCase();
  if (mode === "picture" && String(user.picture || "").trim()) return "";
  const emoji = String(user.avatar_emoji || "").trim();
  if (emoji) return emoji;
  return String(user.initials || "").trim().toUpperCase() || initials(user.name || user.email || "");
};

const profileAvatarPicture = (user = {}) => {
  const mode = String(user.avatar_mode || "").trim().toLowerCase();
  const picture = String(user.picture || "").trim();
  if (!picture) return "";
  if (mode === "picture") return picture;
  if (!mode && !String(user.avatar_emoji || "").trim()) return picture;
  return "";
};

const navItems = [
  { to: "/profile", label: "Profile", icon: UserRound },
  { to: "/profile/leaves", label: "My Leaves", icon: CalendarDays },
  { to: "/profile/stats", label: "My Work", icon: BarChart3 },
  { to: "/profile/notifications", label: "Notifications", icon: Bell },
];

export default function ProfileWorkspaceLayout({ user, onLogout, title, subtitle, children }) {
  const location = useLocation();

  return (
    <div className="profile-shell" data-testid="profile-shell-root">
      <aside className="profile-left" data-testid="profile-left-panel">
        <Link to="/dashboard" className="profile-back-link" data-testid="profile-back-to-app-link">
          <ChevronLeft className="h-4 w-4" />
          Back to app
        </Link>

        <div className="profile-user-card" data-testid="profile-user-card">
          <div
            className="profile-user-avatar"
            style={profileAvatarPicture(user) ? undefined : (user.initials_bg ? { background: user.initials_bg, color: user.initials_text || "#111827" } : undefined)}
            data-testid="profile-user-avatar"
          >
            {profileAvatarPicture(user) ? (
              <img
                src={profileAvatarPicture(user)}
                alt={user.name || user.email}
                className="h-full w-full rounded-[inherit] object-cover"
                data-testid="profile-user-avatar-image"
              />
            ) : profileAvatar(user)}
          </div>
          <div className="profile-user-name" data-testid="profile-user-name">{user.name || user.email}</div>
          <div className="profile-user-email" data-testid="profile-user-email">{user.email}</div>
          <span className="chip chip-ok" data-testid="profile-user-role-chip">{roleLabel(user.role)}</span>
          <div className="profile-user-meta" data-testid="profile-user-meta">
            {user.workspace_domain} · since {memberSinceLabel(user.created_at)}
          </div>
        </div>

        <nav className="profile-nav" data-testid="profile-left-nav">
          {navItems.map((item) => {
            const active = location.pathname === item.to;
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`profile-nav-link ${active ? "active" : ""}`}
                data-testid={`profile-nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <Icon className="h-4 w-4" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <button className="profile-signout" onClick={onLogout} data-testid="profile-left-signout-button">
          <LogOut className="h-4 w-4" />
          Sign out
          <ChevronRight className="h-4 w-4" />
        </button>
      </aside>

      <section className="profile-main" data-testid="profile-main-panel">
        <header className="profile-main-header" data-testid="profile-main-header">
          <h1 className="profile-main-title" data-testid="profile-main-title">{title}</h1>
          <p className="profile-main-subtitle" data-testid="profile-main-subtitle">{subtitle}</p>
        </header>

        {children}
      </section>
    </div>
  );
}
